/**
 * Loads a Mapbox style, strips sources we aren't entitled to, and - for the
 * night theme - actually makes it dark.
 *
 * Two problems this solves:
 *
 * 1. Paid entitlements. The navigation styles bundle `mapbox-traffic-v1` /
 *    `mapbox-incidents-v1`, which free accounts get a 404 for on every tile,
 *    flooding the console and burning head-unit bandwidth. Route congestion
 *    colouring is unaffected (it comes from the Directions API), so we remove
 *    those sources and their layers before handing the style to the map.
 *
 * 2. "Dark mode on mobile isn't really dark." It wasn't. Mapbox's
 *    `navigation-night-v1` paints its land background at hsl(214,17%,31%) -
 *    a mid slate grey. On a phone at full brightness in a dark car that reads
 *    as "daytime with the lights off", which is exactly what was reported. We
 *    now walk the style and pull the lightness of every fill/background down,
 *    while pushing road casings and label text UP, so the result is a genuinely
 *    dark map with MORE road contrast rather than less.
 *
 * 3. Map labels in the wrong language. The navigation styles hardcode every
 *    label to `["coalesce", ["get","name_en"], ["get","name"]]` - English
 *    first - so a driver using the app in Thai reads Thai instructions over an
 *    English map. There is no `name_th` field to switch to: checked against the
 *    live tileset, `mapbox-streets-v8` carries name_ar, name_de, name_en,
 *    name_es, name_fr, name_it, name_ja, name_ko, name_pt, name_ru, name_vi and
 *    both Chinese scripts, and no Thai. What it does carry is `name`, the local
 *    official name - which in Thailand IS the Thai one (Surin is "สุรินทร์").
 *    So the fix is not to find a Thai field but to stop overriding the local
 *    one: prefer `name`, fall back to `name_en` for anywhere that has no local
 *    name.
 *
 * Falls back to the plain style URL if the fetch fails (map still works).
 */

import { MAPBOX_TOKEN, MAP_STYLES } from '../core/config';

type Theme = keyof typeof MAP_STYLES;

const STRIP_SOURCES = new Set(['mapbox-traffic', 'mapbox-incidents']);

/** Layer ids whose colour should be brightened rather than darkened at night. */
const BRIGHTEN = /(label|shield|text|symbol)/i;
/**
 * Road-number shields, left exactly as Mapbox painted them.
 *
 * A shield is text drawn ON TOP of a sprite image - a light rounded plate for
 * `3902`, a coloured badge for a motorway. We cannot recolour the sprite, so
 * brightening the text (which the label rule does: lightness >= 78) puts
 * near-white numerals on a near-white plate and the road numbers vanish - the
 * blank shields reported on the night map. The style already picks a text
 * colour per `shield_text_color` to contrast with its own sprite, so the right
 * move is to leave these layers untouched.
 */
const SHIELD = /shield/i;
/** Road layers: kept bright so the carriageway stands out against the dark. */
const ROADS = /(road|bridge|tunnel|street|motorway|trunk|primary|secondary|highway)/i;

type LabelLang = 'en' | 'local';

const cache = new Map<string, object>();

export async function loadStrippedStyle(
  theme: Theme,
  /** 'local' shows each place's own name - Thai inside Thailand. */
  labels: LabelLang = 'en',
): Promise<object | string> {
  const key = `${theme}:${labels}`;
  const cached = cache.get(key);
  // Hand out a fresh copy each time: mapbox-gl mutates the style object it is
  // given, so returning the cached instance twice feeds it a poisoned style on
  // the second theme switch.
  if (cached) return clone(cached);

  const url = MAP_STYLES[theme];
  try {
    const httpUrl =
      url.replace('mapbox://styles/', 'https://api.mapbox.com/styles/v1/') +
      `?access_token=${MAPBOX_TOKEN}`;
    const res = await fetch(httpUrl, { signal: AbortSignal.timeout(8000) });
    if (!res.ok) return url;
    const style = (await res.json()) as MapboxStyle;

    if (style.sources) {
      for (const name of STRIP_SOURCES) delete style.sources[name];
    }
    if (style.layers) {
      style.layers = style.layers.filter((l) => !l.source || !STRIP_SOURCES.has(l.source));
    }

    if (theme === 'night') deepenNight(style);
    else enrichDay(style);
    if (labels === 'local') preferLocalNames(style);

    cache.set(key, style as object);
    return clone(style as object);
  } catch {
    return url; // offline or blocked - let mapbox-gl load the URL itself
  }
}

/**
 * Flip label priority from English-first to local-first.
 *
 * Every `["get", "name_en"]` anywhere in a text-field becomes
 * `["coalesce", ["get","name"], ["get","name_en"]]`. Done as a substitution on
 * the whole expression tree rather than by matching the one shape the style
 * happens to use today: the layers here already use four different shapes
 * (plain coalesce, `step` on sizerank, `step` on zoom, nested), and a
 * shape-specific rewrite would silently miss the ones it did not anticipate,
 * leaving a map that is Thai in most places and English in a few.
 *
 * The replacement is not descended into, so it cannot rewrite itself.
 */
function preferLocalNames(style: MapboxStyle): void {
  for (const layer of style.layers ?? []) {
    const layout = (layer as { layout?: Record<string, unknown> }).layout;
    if (!layout || layout['text-field'] === undefined) continue;
    layout['text-field'] = localise(layout['text-field']);
  }
}

function localise(node: unknown): unknown {
  if (!Array.isArray(node)) return node;
  if (node.length === 2 && node[0] === 'get' && node[1] === 'name_en') {
    return ['coalesce', ['get', 'name'], ['get', 'name_en']];
  }
  return node.map(localise);
}

interface MapboxStyle {
  sources?: Record<string, unknown>;
  layers?: Array<{
    id?: string;
    type?: string;
    source?: string;
    paint?: Record<string, unknown>;
  }>;
}

function clone<T>(v: T): T {
  return typeof structuredClone === 'function'
    ? structuredClone(v)
    : (JSON.parse(JSON.stringify(v)) as T);
}

/**
 * Push the whole style into true night: land and water go near-black, roads
 * stay legible, labels get brighter. Colour expressions are walked recursively
 * because Mapbox styles bury colours inside `interpolate` / `match` arrays.
 */
function deepenNight(style: MapboxStyle): void {
  for (const layer of style.layers ?? []) {
    if (!layer.paint) continue;
    if (SHIELD.test(layer.id ?? '')) continue;
    const isLabel = BRIGHTEN.test(layer.id ?? '') || layer.type === 'symbol';
    const isRoad = ROADS.test(layer.id ?? '');

    for (const [prop, value] of Object.entries(layer.paint)) {
      if (!/color/i.test(prop)) continue;
      layer.paint[prop] = mapColors(value, (hsl) => {
        if (isLabel) {
          // Labels: high lightness, low saturation - crisp white-ish text.
          return { ...hsl, s: Math.min(hsl.s, 15), l: Math.max(hsl.l, 78) };
        }
        if (isRoad) {
          // Roads: lift them clear of the background instead of dimming them.
          return { ...hsl, s: Math.min(hsl.s, 20), l: clamp(hsl.l * 0.72 + 10, 16, 46) };
        }
        // Everything else - land, water, landuse - goes properly dark.
        return { ...hsl, s: hsl.s * 0.55, l: clamp(hsl.l * 0.3, 3, 16) };
      });
    }
  }
}

/**
 * Put some colour back into the day map.
 *
 * `navigation-day-v1` is deliberately desaturated - grey roads on a near-white
 * ground with grey buildings - which reads as washed out on a phone in
 * daylight and makes road classes hard to tell apart at a glance. Rather than
 * swapping to a different style and losing the navigation-tuned road widths,
 * every colour has its saturation lifted: greens become green, water becomes
 * blue, and the motorway ambers separate clearly from ordinary streets.
 *
 * Labels are left alone - tinting text hurts legibility, which is the one
 * thing that must not regress.
 */
function enrichDay(style: MapboxStyle): void {
  for (const layer of style.layers ?? []) {
    if (!layer.paint) continue;
    if (BRIGHTEN.test(layer.id ?? '') || layer.type === 'symbol') continue;
    const isRoad = ROADS.test(layer.id ?? '');

    for (const [prop, value] of Object.entries(layer.paint)) {
      if (!/color/i.test(prop)) continue;
      layer.paint[prop] = mapColors(value, (hsl) => {
        // Near-white and near-black carry no hue to amplify; boosting them
        // just makes dirty greys.
        if (hsl.l > 96 || hsl.l < 6) return hsl;
        if (isRoad) {
          // Keep roads bright but let their hue show, so classes differ.
          return { ...hsl, s: clamp(hsl.s * 2.1 + 8, 0, 82), l: clamp(hsl.l, 42, 97) };
        }
        return { ...hsl, s: clamp(hsl.s * 1.9 + 6, 0, 78), l: clamp(hsl.l * 0.99, 8, 96) };
      });
    }
  }
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

interface Hsl {
  h: number;
  s: number;
  l: number;
  a: number;
}

/** Walk any paint value, transforming every colour literal it contains. */
function mapColors(value: unknown, fn: (hsl: Hsl) => Hsl): unknown {
  if (typeof value === 'string') {
    const hsl = parseColor(value);
    return hsl ? formatHsl(fn(hsl)) : value;
  }
  if (Array.isArray(value)) return value.map((v) => mapColors(v, fn));
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = k === 'stops' || k === 'default' ? mapColors(v, fn) : v;
    }
    return out;
  }
  return value;
}

function parseColor(input: string): Hsl | null {
  const s = input.trim();

  const hslMatch = /^hsla?\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*(?:,\s*([\d.]+)\s*)?\)$/i.exec(s);
  if (hslMatch) {
    return {
      h: Number(hslMatch[1]),
      s: Number(hslMatch[2]),
      l: Number(hslMatch[3]),
      a: hslMatch[4] === undefined ? 1 : Number(hslMatch[4]),
    };
  }

  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s);
  if (hex) {
    const h = hex[1];
    const full =
      h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const r = parseInt(full.slice(0, 2), 16) / 255;
    const g = parseInt(full.slice(2, 4), 16) / 255;
    const b = parseInt(full.slice(4, 6), 16) / 255;
    return { ...rgbToHsl(r, g, b), a: 1 };
  }

  const rgbMatch = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i.exec(s);
  if (rgbMatch) {
    return {
      ...rgbToHsl(Number(rgbMatch[1]) / 255, Number(rgbMatch[2]) / 255, Number(rgbMatch[3]) / 255),
      a: rgbMatch[4] === undefined ? 1 : Number(rgbMatch[4]),
    };
  }

  return null;
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l: l * 100 };

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h * 60, s: s * 100, l: l * 100 };
}

function formatHsl({ h, s, l, a }: Hsl): string {
  const H = Math.round(h * 10) / 10;
  const S = Math.round(clamp(s, 0, 100) * 10) / 10;
  const L = Math.round(clamp(l, 0, 100) * 10) / 10;
  return a >= 1 ? `hsl(${H}, ${S}%, ${L}%)` : `hsla(${H}, ${S}%, ${L}%, ${a})`;
}
